// ============================================================
// Settings.tsx
// User profile and settings screen — personal preferences only.
//
// Layout (Option B redesign — casevo-settings-redesign.html): the shared
// 1180px page head + a 236px left SECTION-NAV and the selected section's
// surface cards on the right. Below lg the nav collapses to a horizontal
// tab bar above the cards.
//
// Sections (regrouped by topic):
//   - Account            identity (avatar + Change avatar), Profile (name
//                        edit, email, role), Password (change + forgot),
//                        Two-factor authentication
//   - Email & Microsoft  Microsoft account connect/disconnect, Email signature
//   - Appearance         Theme (light · dark · device · automatic), Screen zoom & pan, Timeline colours
//   - Notifications      the 6 email-notification toggles (save per toggle)
//   - Firm               Firm name / ID (copy) / member since, Session sign-out
// Sign out is also a nav item. The selected section is remembered on this
// device (localStorage) — a one-shot pendingSettingsSection (header avatar
// click) or a Microsoft OAuth return (?ms=…) overrides it.
//
// Firm-admin tools (Team management + Deadline Type catalogue) live in
// AdminPage.tsx, not here.
// ============================================================
import SignatureUploadCard from '../components/settings/SignatureUploadCard'
import React, { useState, useEffect } from 'react'
import { useCasevoStore } from '../store/useCasevoStore'
import { navigateTo, navigateWithin, usePlaceParam, initialPlaceParam } from '../navigation/navigate'
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard'
import type { SettingsSection } from '../store/useCasevoStore'
import { updateMeApi, changePasswordApi, requestPasswordResetApi, msStatusApi, requestEmailChangeApi, adminChangeUserEmailApi } from '../api/casevoApi'
import ThemeSettings from '../components/settings/ThemeSettings'
import DeadlineReminderPrefs from '../components/settings/DeadlineReminderPrefs'
import type { NotificationPreferences, BooleanNotificationPreference } from '../types/notifications.types'
import { DEFAULT_NOTIFICATION_PREFERENCES } from '../types/notifications.types'
import AppHeader from '../components/layout/AppHeader'
import BackButton from '../components/layout/BackButton'
import { SidebarHamburger } from '../components/layout/Sidebar'
import UserAvatar from '../components/shared/UserAvatar'
import AvatarChooserModal from '../components/shared/AvatarChooserModal'
import ConfirmDialogue from '../components/shared/ConfirmDialogue'
import EmailChangeDialog from '../components/shared/EmailChangeDialog'
import { UserTimelineColoursPanel } from '../components/shared/UserTimelineColoursPanel'
import TwoFactorPanel from '../components/shared/TwoFactorPanel'
import MicrosoftPanel, { peekMicrosoftReturn } from '../components/shared/MicrosoftPanel'
import EmailSignaturePanel from '../components/shared/EmailSignaturePanel'
import { ErrorNote } from '../components/shared/ActionBox'
import { useZoomPref } from '../utils/zoomPref'
import {
  SETTINGS_CARD, SettingsCard, SettingsRow, RowSmall, SettingsToggleRow, SettingsSwitch,
  SettingsBadge, SettingsSaved, SIcon, sInput,
  S_BTN_PRIMARY, S_BTN_GHOST, S_BTN_GHOST_SM, S_BTN_DANGER_SM,
  S_FIELDS, S_LABEL, S_FORMFOOT, S_FIELD_ERROR, S_LINK, S_NOTE,
  SectionNavItem,
  SectionNavPill,
} from '../components/settings/settingsUi'
import type { SettingsIconName } from '../components/settings/settingsUi'

// Matches RegisterScreen's validation pattern
type FieldErrors = Partial<Record<'firstName' | 'lastName', string>>
type PasswordFieldErrors = Partial<Record<'currentPassword' | 'newPassword' | 'confirmPassword', string>>

type EditMode = 'idle' | 'editing' | 'saving'

// Inline spinner — mirrors RegisterScreen's Spinner subcomponent
const Spinner: React.FC = () => (
  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z" />
  </svg>
)

// ── Section nav model ────────────────────────────────────────
const SECTION_STORAGE_KEY = 'casevo_settings_section'
const SECTIONS: { key: SettingsSection; label: string; icon: SettingsIconName; title: string; sub: string }[] = [
  { key: 'account',       label: 'Account',           icon: 'user',     title: 'Account',           sub: 'Your profile, password and sign-in security' },
  { key: 'email',         label: 'Email & Microsoft', icon: 'plug',     title: 'Email & Microsoft', sub: 'Your Microsoft connection and email signature' },
  { key: 'appearance',    label: 'Appearance',        icon: 'sun',      title: 'Appearance',        sub: 'Theme, screen zoom and timeline colours' },
  { key: 'notifications', label: 'Notifications',     icon: 'bell',     title: 'Notifications',     sub: 'Choose which emails Casevo sends you' },
  { key: 'firm',          label: 'Firm',              icon: 'building', title: 'Firm',              sub: 'Your firm and this device’s session' },
]
const isSection = (v: unknown): v is SettingsSection =>
  typeof v === 'string' && SECTIONS.some((s) => s.key === v)

// Initial section: the place's own (?view=settings&section=…, a refresh /
// Back) first; then a Microsoft OAuth return (?ms=…) → Email & Microsoft (so
// MicrosoftPanel mounts, shows the result banner and strips the param);
// else the remembered section; else Account. The one-shot
// pendingSettingsSection is applied on mount (see effect below).
const initialSection = (): SettingsSection => {
  try {
    const fromPlace = initialPlaceParam('section')
    if (isSection(fromPlace)) return fromPlace
    if (peekMicrosoftReturn()) return 'email'
    const saved = window.localStorage.getItem(SECTION_STORAGE_KEY)
    if (isSection(saved)) return saved
  } catch { /* storage blocked — fall through */ }
  return 'account'
}

// The 7 live email-notification toggles (emailDailyDigest stays omitted —
// no digest backend yet; its key/default is kept in notifications.types.ts
// so re-enabling is a one-line add here once that pipeline ships).
// In-app (while Casevo is open): a toast and / or a chime when a new bell
// notification arrives (NotificationsBell). Stored in the same
// notificationPreferences JSON as the email toggles — no schema.
const IN_APP_NOTIFICATION_ROWS: { key: BooleanNotificationPreference; label: string; desc: string }[] = [
  { key: 'popupOnNotification', label: 'Show a popup for new notifications', desc: 'A brief message in the corner of Casevo — click it to open the notification.' },
  { key: 'soundOnNotification', label: 'Play a sound for new notifications', desc: 'A short chime while Casevo is open.' },
]
const NOTIFICATION_ROWS: { key: BooleanNotificationPreference; label: string }[] = [
  { key: 'emailOnTaskAssigned', label: "Email me when I'm assigned a task" },
  { key: 'emailOnTaskCompleted', label: 'Email me when a task I created is completed' },
  { key: 'emailOnCaseAssigned', label: "Email me when I'm assigned a case" },
  { key: 'emailBeforeDeadline', label: 'Email me when a case deadline is approaching' },
  { key: 'emailOnDayReminder', label: 'Email me when a reminder I set falls due' },
  { key: 'emailOnTaskDue', label: 'Email me when a task assigned to me is due' },
  { key: 'emailOnBillApproval', label: 'Email me about bill approvals (a bill sent for my approval, or mine approved / sent back)' },
]

const Settings: React.FC = () => {
  const { authUser, setAuthUser } = useCasevoStore()
  const pendingSettingsSection = useCasevoStore((s) => s.pendingSettingsSection)
  const setPendingSettingsSection = useCasevoStore((s) => s.setPendingSettingsSection)
  // Avatar chooser modal (opened from the identity card / nav avatar).
  const [avatarModalOpen, setAvatarModalOpen] = useState(false)

  // Selected section — remembered per device (see initialSection).
  // The section is part of the place (?view=settings&section=…): choosing one
  // is a history step (Back returns to the section you were on) and asks the
  // unsaved-changes guard first; a refresh / Back-Forward re-opens it.
  const [activeSection, setActiveSection] = useState<SettingsSection>(initialSection)
  const showSection = (key: SettingsSection): void => {
    setActiveSection(key)
    try { window.localStorage.setItem(SECTION_STORAGE_KEY, key) } catch { /* ignore */ }
  }
  const selectSection = (key: SettingsSection): void => {
    if (key === activeSection) return
    void navigateWithin(() => {
      // Leaving the section drops its unsaved drafts (the guard asked).
      cancelEditing()
      cancelChangingPassword()
      showSection(key)
    })
  }
  usePlaceParam('section', activeSection, (v) => { if (isSection(v)) showSection(v) })

  // Microsoft connection state for the nav sub-line. Fetched here so it's
  // live even when the Email section isn't open; MicrosoftPanel reports
  // changes (connect return / disconnect) back via onStatusChange.
  // undefined = loading, null = unknown (fetch failed).
  const [msConnected, setMsConnected] = useState<boolean | null | undefined>(undefined)
  useEffect(() => {
    let cancelled = false
    msStatusApi()
      .then((s) => { if (!cancelled) setMsConnected(s.connected) })
      .catch(() => { if (!cancelled) setMsConnected(null) })
    return () => { cancelled = true }
  }, [])

  // Copy-to-clipboard state
  const [copied, setCopied] = useState(false)
  // "Request email change" dialog (notifies the firm's admins).
  const [emailRequestOpen, setEmailRequestOpen] = useState(false)
  // Sign-out confirmation dialog (see signOut below).
  const [signOutConfirmOpen, setSignOutConfirmOpen] = useState(false)

  // Name editing state
  const [mode, setMode] = useState<EditMode>('idle')
  const [editFirstName, setEditFirstName] = useState('')
  const [editLastName, setEditLastName] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [serverError, setServerError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  // Password change state — mirrors the name-edit pattern above
  const [passwordMode, setPasswordMode] = useState<EditMode>('idle')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordFieldErrors, setPasswordFieldErrors] = useState<PasswordFieldErrors>({})
  const [passwordServerError, setPasswordServerError] = useState<string | null>(null)
  const [passwordSaved, setPasswordSaved] = useState(false)
  // Unsaved-changes guard (hooks/useUnsavedChangesGuard): an open name edit
  // that differs from the saved name, or a password change with anything
  // typed. Leaving Settings, switching section, Back or a refresh asks first.
  const nameDirty = mode === 'editing' && !!authUser
    && (editFirstName !== authUser.firstName || editLastName !== authUser.lastName)
  const passwordDirty = passwordMode !== 'idle' && (currentPassword !== '' || newPassword !== '' || confirmPassword !== '')
  useUnsavedChangesGuard(nameDirty || passwordDirty)
  // "Forgot password?" — reuses the SAME reset flow as the login page
  // (POST /api/auth/request-reset → emailed link → AcceptResetScreen), sent
  // to the signed-in user's own email.
  const [forgotState, setForgotState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  // Show/hide toggles per field — mirrors LoginScreen's pattern
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  // Screen zoom & pan — per-device accessibility preference (localStorage-
  // backed via useZoomPref). Off by default. No backend write.
  const zoomEnabled = useZoomPref((s) => s.enabled)
  const setZoomEnabled = useZoomPref((s) => s.setEnabled)

  // Wordmark = "go home" (a navigation like any other).
  const handleBack = () => {
    void navigateTo({ view: 'caseList' })
  }

  // One-shot: the header avatar click asks to land on a specific section
  // ('account' — identity card + Change avatar). Honour it on mount, then
  // CLEAR the slot so a normal open uses the remembered section.
  useEffect(() => {
    if (pendingSettingsSection) {
      showSection(pendingSettingsSection)
      setPendingSettingsSection(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── Copy Firm ID ───────────────────────────────────────────
  const handleCopyFirmId = async () => {
    if (!authUser) return
    try {
      await navigator.clipboard.writeText(authUser.firmId)
      setCopied(true)
    } catch {
      // Clipboard API blocked — silently keep "Copy" state
    }
  }

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(timer)
  }, [copied])

  // ── "Saved ✓" feedback timer ───────────────────────────────
  useEffect(() => {
    if (!saved) return
    const timer = setTimeout(() => setSaved(false), 2000)
    return () => clearTimeout(timer)
  }, [saved])
  // Auto-fade for the password "Saved ✓" feedback
  useEffect(() => {
    if (!passwordSaved) return
    const timer = setTimeout(() => setPasswordSaved(false), 2000)
    return () => clearTimeout(timer)
  }, [passwordSaved])

  // ── Password change helpers ─────────────────────────────────

  const startChangingPassword = () => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setPasswordFieldErrors({})
    setPasswordServerError(null)
    setPasswordMode('editing')
  }

  const cancelChangingPassword = () => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setPasswordFieldErrors({})
    setPasswordServerError(null)
    setPasswordMode('idle')
  }

  // Mirrors backend rules verbatim: presence, length 8-100, new ≠ current.
  // Confirm-match is frontend-only — backend never sees the confirm field.
  const validatePassword = (): PasswordFieldErrors => {
    const errs: PasswordFieldErrors = {}
    if (!currentPassword.trim()) {
      errs.currentPassword = 'Current password is required'
    }
    if (!newPassword.trim()) {
      errs.newPassword = 'New password is required'
    } else if (newPassword.length < 8) {
      errs.newPassword = 'New password must be at least 8 characters'
    } else if (newPassword.length > 100) {
      errs.newPassword = 'New password must be at most 100 characters'
    } else if (newPassword === currentPassword) {
      errs.newPassword = 'New password must be different from current password'
    }
    if (!confirmPassword.trim()) {
      errs.confirmPassword = 'Please confirm your new password'
    } else if (confirmPassword !== newPassword) {
      errs.confirmPassword = 'Passwords do not match'
    }
    return errs
  }

  const clearPasswordFieldError = (key: keyof PasswordFieldErrors) => {
    setPasswordFieldErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  const handleChangePassword = async () => {
    const errs = validatePassword()
    if (Object.keys(errs).length > 0) {
      setPasswordFieldErrors(errs)
      setPasswordServerError(null)
      return
    }
    try {
      setPasswordMode('saving')
      setPasswordFieldErrors({})
      setPasswordServerError(null)
      const changed = await changePasswordApi({ currentPassword, newPassword })
      // The server revokes every session issued before the change (incl.
      // this tab's) and returns a fresh token — swap it in so we stay signed in.
      // Token-only swap (NOT setAuth — that re-seeds the viewing-pane toggles
      // from a possibly stale authUser copy).
      if (changed.token) {
        localStorage.setItem('casevo_token', changed.token)
        useCasevoStore.setState({ authToken: changed.token })
      }
      // Success — clear all fields, drop back to idle, show fading tick
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setPasswordMode('idle')
      setPasswordSaved(true)
    } catch (err) {
      // Backend error strings are designed to be shown verbatim
      const message = err instanceof Error && err.message
        ? err.message
        : 'Could not connect to the server. Please try again.'
      setPasswordServerError(message)
      setPasswordMode('editing')
    }
  }

  const handleForgotPassword = async (): Promise<void> => {
    if (!authUser?.email || forgotState === 'sending') return
    setForgotState('sending')
    try {
      await requestPasswordResetApi(authUser.email)
      setForgotState('sent')
    } catch {
      setForgotState('error')
    }
  }

  // ── Name editing helpers ───────────────────────────────────

  const startEditing = () => {
    if (!authUser) return
    setEditFirstName(authUser.firstName)
    setEditLastName(authUser.lastName)
    setFieldErrors({})
    setServerError(null)
    setMode('editing')
  }

  const cancelEditing = () => {
    setFieldErrors({})
    setServerError(null)
    setMode('idle')
  }

  // Mirrors RegisterScreen's validate() pattern
  const validate = (): FieldErrors => {
    const errs: FieldErrors = {}
    if (!editFirstName.trim()) {
      errs.firstName = 'Please enter your first name'
    } else if (editFirstName.trim().length > 100) {
      errs.firstName = 'First name is too long'
    }
    if (!editLastName.trim()) {
      errs.lastName = 'Please enter your last name'
    } else if (editLastName.trim().length > 100) {
      errs.lastName = 'Last name is too long'
    }
    return errs
  }

  // Clears one field's error when the user starts typing again
  const clearFieldError = (key: keyof FieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  const handleSave = async () => {
    // Client-side validation — short-circuit on failure
    const errs = validate()
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs)
      setServerError(null)
      return
    }

    try {
      setMode('saving')
      setFieldErrors({})
      setServerError(null)

      const updated = await updateMeApi({
        firstName: editFirstName.trim(),
        lastName: editLastName.trim(),
      })
      setAuthUser(updated)
      setMode('idle')
      setSaved(true)
    } catch (err) {
      // Widened from a generic-only message: surface the backend's
      // specific text (e.g. validation feedback unwrapped from the
      // response envelope) when present, fall back to the existing
      // generic for transport failures.
      const message = err instanceof Error && err.message
        ? err.message
        : 'Could not connect to the server. Please try again.'
      setServerError(message)
      setMode('editing')
    }
  }

  // Guard
  if (!authUser) {
    return (
      <div className="min-h-screen bg-casevo-offwhite flex items-center justify-center">
        <p className="text-casevo-mid text-sm">Not signed in.</p>
      </div>
    )
  }

  const fullName = `${authUser.firstName} ${authUser.lastName}`.trim() || '—'
  const isSaving = mode === 'saving'
  const isEditing = mode === 'editing' || mode === 'saving'
  const isPasswordOpen = passwordMode === 'editing' || passwordMode === 'saving'
  const roleLabel = authUser.role ? authUser.role.charAt(0).toUpperCase() + authUser.role.slice(1) : '—'
  const firmName = authUser.firmName || null
  const isAdmin = authUser.role === 'admin'

  // "Member since" — only shown when the backend provides createdAt
  const memberSince = authUser.createdAt
    ? new Date(authUser.createdAt).toLocaleString('en-GB', { month: 'long', year: 'numeric' })
    : null

  // ── Notifications: merged prefs + per-toggle save ───────────
  // Saved prefs merged over defaults at render time, so missing keys
  // (brand-new accounts, prefs added later) get sensible fallbacks.
  const prefs: NotificationPreferences = {
    ...DEFAULT_NOTIFICATION_PREFERENCES,
    ...(authUser.notificationPreferences ?? {}),
  }
  const notificationsOn = NOTIFICATION_ROWS.filter((r) => prefs[r.key]).length
  // Optimistic: push the next state to the store so the switch flips with
  // no lag, then save the FULL merged object via PATCH /api/auth/me; the
  // response overwrites with the canonical value. Roll back on failure.
  const handleToggleNotification = async (key: BooleanNotificationPreference): Promise<void> => {
    const next: NotificationPreferences = { ...prefs, [key]: !prefs[key] }
    setAuthUser({ ...authUser, notificationPreferences: next })
    try {
      const updated = await updateMeApi({ notificationPreferences: next })
      setAuthUser(updated)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[Settings] Failed to save notification prefs:', err)
      setAuthUser({ ...authUser, notificationPreferences: prefs })
    }
  }

  // Sign out is confirmed first (same dialog + copy as the Sidebar's Sign
  // Out — Sidebar.tsx ConfirmDialogue). Both Settings entry points (nav
  // item + Session card) open it; only the dialog's confirm clears auth.
  const signOut = (): void => setSignOutConfirmOpen(true)

  // Nav sub-lines (live state).
  const navSub = (key: SettingsSection): string | null => {
    if (key === 'account') return 'Profile · security'
    if (key === 'notifications') return `${notificationsOn} of ${NOTIFICATION_ROWS.length} on`
    // undefined = loading, null = status fetch failed → show nothing rather
    // than a misleading "Not connected".
    if (key === 'email') return msConnected == null ? null : msConnected ? 'Connected' : 'Not connected'
    return null
  }
  const current = SECTIONS.find((s) => s.key === activeSection) ?? SECTIONS[0]

  // Show/hide toggle inside a password input.
  const revealBtn = (shown: boolean, toggle: () => void, what: string) => (
    <button
      type="button"
      onClick={toggle}
      disabled={passwordMode === 'saving'}
      aria-label={`${shown ? 'Hide' : 'Show'} ${what}`}
      className="absolute right-[12px] top-1/2 -translate-y-1/2 text-[11px] font-semibold tracking-[0.1em] uppercase text-casevo-brand dark:text-casevo-mid hover:text-casevo-forest disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none casevo-focus rounded-sm"
    >
      {shown ? 'Hide' : 'Show'}
    </button>
  )

  const forgotLink = (
    <button
      type="button"
      onClick={() => { void handleForgotPassword() }}
      disabled={forgotState === 'sending' || forgotState === 'sent'}
      className={S_LINK}
    >
      {forgotState === 'sending' ? 'Sending…' : forgotState === 'sent' ? 'Reset link sent' : 'Email me a reset link'}
    </button>
  )
  const forgotMessages = (
    <>
      {forgotState === 'sent' && (
        <p className={`${S_NOTE} mt-[6px]`} role="status">
          We&apos;ve emailed a password reset link to <span className="font-medium text-casevo-forest">{authUser.email}</span>.
          Resetting your password signs you out everywhere, so you&apos;ll log in again with the new one.
        </p>
      )}
      {forgotState === 'error' && (
        <p className={S_FIELD_ERROR} role="alert">Couldn&apos;t send the reset email. Please try again.</p>
      )}
    </>
  )

  // ── Section content ─────────────────────────────────────────
  const accountSection = (
    <>
      {/* Identity (.ident) */}
      <div className={SETTINGS_CARD}>
        <div className="flex flex-wrap items-center gap-[18px] px-[22px] py-[20px]">
          <button
            type="button"
            onClick={() => setAvatarModalOpen(true)}
            aria-label="Change avatar"
            className="relative shrink-0 rounded-full focus:outline-none casevo-focus"
          >
            <UserAvatar userId={authUser.id} fallbackName={`${authUser.firstName} ${authUser.lastName}`.trim() || authUser.email} size="xl" title={false} />
            <span className="absolute -right-[2px] -bottom-[2px] w-[24px] h-[24px] rounded-full bg-casevo-surface border border-casevo-border text-casevo-brand dark:text-[#7fd1a8] grid place-items-center">
              <SIcon name="cam" size={12} />
            </span>
          </button>
          <div className="flex-1 min-w-[180px]">
            <div className="text-[19px] font-semibold text-casevo-forest break-words">{fullName}</div>
            <div className="text-[13.5px] text-casevo-text-soft mt-[2px] break-all">{authUser.email}</div>
            <div className="flex flex-wrap gap-[8px] mt-[8px]">
              <SettingsBadge on dot>{roleLabel}</SettingsBadge>
              {(firmName || memberSince) && (
                <SettingsBadge>{[firmName, memberSince ? `since ${memberSince}` : null].filter(Boolean).join(' · ')}</SettingsBadge>
              )}
            </div>
          </div>
          <button type="button" onClick={() => setAvatarModalOpen(true)} className={S_BTN_GHOST_SM}>
            <SIcon name="cam" size={14} />
            Change avatar
          </button>
        </div>
      </div>

      {/* Profile */}
      <SettingsCard
        icon="user"
        title="Profile"
        sub="Your name as it appears on cases, tasks and emails."
        right={!isEditing && (
          <button type="button" onClick={startEditing} className={S_BTN_GHOST_SM}>
            <SIcon name="pen" size={14} />
            Edit
          </button>
        )}
      >
        {isEditing ? (
          <div>
            {serverError && <ErrorNote className="mt-[12px]">{serverError}</ErrorNote>}
            <div className={S_FIELDS}>
              <div>
                <label htmlFor="settings-first-name" className={S_LABEL}>First name</label>
                <input
                  id="settings-first-name"
                  type="text"
                  value={editFirstName}
                  onChange={(e) => { setEditFirstName(e.target.value); clearFieldError('firstName') }}
                  className={sInput(!!fieldErrors.firstName)}
                  disabled={isSaving}
                  aria-invalid={!!fieldErrors.firstName}
                  autoFocus
                />
                {fieldErrors.firstName && <p className={S_FIELD_ERROR}>{fieldErrors.firstName}</p>}
              </div>
              <div>
                <label htmlFor="settings-last-name" className={S_LABEL}>Last name</label>
                <input
                  id="settings-last-name"
                  type="text"
                  value={editLastName}
                  onChange={(e) => { setEditLastName(e.target.value); clearFieldError('lastName') }}
                  className={sInput(!!fieldErrors.lastName)}
                  disabled={isSaving}
                  aria-invalid={!!fieldErrors.lastName}
                />
                {fieldErrors.lastName && <p className={S_FIELD_ERROR}>{fieldErrors.lastName}</p>}
              </div>
            </div>
            <div className={S_FORMFOOT}>
              <span className="flex-1" />
              <button type="button" onClick={cancelEditing} disabled={isSaving} className={S_BTN_GHOST}>Cancel</button>
              <button type="button" onClick={() => { void handleSave() }} disabled={isSaving} aria-busy={isSaving} className={S_BTN_PRIMARY}>
                {isSaving && <Spinner />}
                {isSaving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <SettingsRow label="First name">{authUser.firstName || '—'}</SettingsRow>
            <SettingsRow label="Last name" action={saved ? <SettingsSaved /> : undefined}>{authUser.lastName || '—'}</SettingsRow>
            <SettingsRow
              label="Email"
              action={
                <button type="button" onClick={() => setEmailRequestOpen(true)} className={S_BTN_GHOST_SM}>
                  {isAdmin ? 'Change email' : 'Request email change'}
                </button>
              }
            >
              {authUser.email}
              {/* Email is the login identity (updateMe never accepts it).
                  ROLE-AWARE: an ADMIN changes their own directly via the
                  admin endpoint (PATCH /api/users/:ownId/email — validated,
                  pending-invite-guarded, revokes their sessions); a MEMBER
                  can only REQUEST it (POST /api/me/email-change-request →
                  bell + email to the firm's admins). */}
              <RowSmall>{isAdmin ? 'Used to sign in. Changing it signs you out everywhere.' : 'Used to sign in — an admin can change this.'}</RowSmall>
            </SettingsRow>
            <SettingsRow label="Role">{roleLabel}</SettingsRow>
            {/* B3 — set by an admin (Admin › Team); printed when you sign a bill. */}
            <SettingsRow label="Job title">{authUser.jobTitle?.trim() || <span className="text-casevo-text-soft">Not set. An admin can add it in Admin › Team.</span>}</SettingsRow>
          </>
        )}
      </SettingsCard>

      {/* B3b — your own signature image, printed on bills you sign. */}
      <SignatureUploadCard />

      {/* Password */}
      <SettingsCard
        icon="key"
        title="Password"
        sub="Change the password you use to sign in."
        right={!isPasswordOpen && (
          <button type="button" onClick={startChangingPassword} className={S_BTN_GHOST_SM}>Change password</button>
        )}
      >
        {isPasswordOpen ? (
          <div>
            {passwordServerError && <ErrorNote className="mt-[12px]">{passwordServerError}</ErrorNote>}
            <div className={`${S_FIELDS} sm:grid-cols-1`}>
              <div>
                <label htmlFor="settings-current-password" className={S_LABEL}>Current password</label>
                <div className="relative">
                  <input
                    id="settings-current-password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => { setCurrentPassword(e.target.value); clearPasswordFieldError('currentPassword') }}
                    className={`${sInput(!!passwordFieldErrors.currentPassword)} pr-[60px]`}
                    disabled={passwordMode === 'saving'}
                    aria-invalid={!!passwordFieldErrors.currentPassword}
                    autoComplete="current-password"
                    autoFocus
                  />
                  {revealBtn(showCurrentPassword, () => setShowCurrentPassword(!showCurrentPassword), 'current password')}
                </div>
                {passwordFieldErrors.currentPassword && <p className={S_FIELD_ERROR}>{passwordFieldErrors.currentPassword}</p>}
                <div className={`${S_NOTE} mt-[7px]`}>
                  Forgotten it? {forgotLink} — resetting signs you out everywhere.
                </div>
                {forgotMessages}
              </div>
            </div>
            <div className={S_FIELDS}>
              <div>
                <label htmlFor="settings-new-password" className={S_LABEL}>New password</label>
                <div className="relative">
                  <input
                    id="settings-new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); clearPasswordFieldError('newPassword') }}
                    className={`${sInput(!!passwordFieldErrors.newPassword)} pr-[60px]`}
                    disabled={passwordMode === 'saving'}
                    aria-invalid={!!passwordFieldErrors.newPassword}
                    autoComplete="new-password"
                  />
                  {revealBtn(showNewPassword, () => setShowNewPassword(!showNewPassword), 'new password')}
                </div>
                {passwordFieldErrors.newPassword && <p className={S_FIELD_ERROR}>{passwordFieldErrors.newPassword}</p>}
              </div>
              <div>
                <label htmlFor="settings-confirm-password" className={S_LABEL}>Confirm new password</label>
                <div className="relative">
                  <input
                    id="settings-confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); clearPasswordFieldError('confirmPassword') }}
                    className={`${sInput(!!passwordFieldErrors.confirmPassword)} pr-[60px]`}
                    disabled={passwordMode === 'saving'}
                    aria-invalid={!!passwordFieldErrors.confirmPassword}
                    autoComplete="new-password"
                    placeholder="Re-enter new password"
                  />
                  {revealBtn(showConfirmPassword, () => setShowConfirmPassword(!showConfirmPassword), 'confirmation')}
                </div>
                {passwordFieldErrors.confirmPassword && <p className={S_FIELD_ERROR}>{passwordFieldErrors.confirmPassword}</p>}
              </div>
            </div>
            <div className={S_FORMFOOT}>
              <span className={S_NOTE}>At least 8 characters.</span>
              <span className="flex-1" />
              <button type="button" onClick={cancelChangingPassword} disabled={passwordMode === 'saving'} className={S_BTN_GHOST}>Cancel</button>
              <button
                type="button"
                onClick={() => { void handleChangePassword() }}
                disabled={passwordMode === 'saving'}
                aria-busy={passwordMode === 'saving'}
                className={S_BTN_PRIMARY}
              >
                {passwordMode === 'saving' && <Spinner />}
                {passwordMode === 'saving' ? 'Saving…' : 'Update password'}
              </button>
            </div>
          </div>
        ) : (
          <SettingsToggleRow
            title="Password"
            desc={<>Forgotten it? {forgotLink}{forgotMessages}</>}
            control={passwordSaved ? <SettingsSaved>Password updated</SettingsSaved> : undefined}
          />
        )}
      </SettingsCard>

      {/* Two-factor authentication — owns its own load/enrol/disable. */}
      <TwoFactorPanel />
    </>
  )

  const emailSection = (
    <>
      <MicrosoftPanel onStatusChange={setMsConnected} />
      <EmailSignaturePanel />
    </>
  )

  const appearanceSection = (
    <>
      <SettingsCard icon="sun" title="Appearance" sub="The theme is saved to your account and follows you to any device. Screen zoom is per device.">
        {/* Light · Dark · Match my device · Automatic (day/night). */}
        <ThemeSettings />
        <SettingsToggleRow
          title="Screen zoom & pan"
          desc="Adds a magnifier in the corner to zoom the page and pan by moving your mouse to the edges."
          control={<SettingsSwitch checked={zoomEnabled} label="Screen zoom and pan" onChange={() => setZoomEnabled(!zoomEnabled)} />}
        />
      </SettingsCard>
      {/* Per-user timeline colour overrides — owns its own load + save. */}
      <UserTimelineColoursPanel />
    </>
  )

  const notificationsSection = (
    <>
    <SettingsCard icon="bell" title="Email notifications" sub="Choose which emails Casevo sends you. Changes save instantly.">
      {NOTIFICATION_ROWS.map((row) => (
        <SettingsToggleRow
          key={row.key}
          title={row.label}
          titleWeight="normal"
          control={
            <SettingsSwitch
              checked={prefs[row.key]}
              label={row.label}
              onChange={() => { void handleToggleNotification(row.key) }}
            />
          }
        />
      ))}
    </SettingsCard>
    {/* Round 2 — personal extra deadline warnings (on top of the firm's). */}
    <DeadlineReminderPrefs />
    {/* In-app only (while Casevo is open) — not email, not a browser / OS notification. */}
    <SettingsCard icon="bell" title="In-app notifications" sub="What happens in Casevo when a new notification arrives in the bell. Changes save instantly.">
      {IN_APP_NOTIFICATION_ROWS.map((row) => (
        <SettingsToggleRow
          key={row.key}
          title={row.label}
          desc={row.desc}
          titleWeight="normal"
          control={
            <SettingsSwitch
              checked={prefs[row.key]}
              label={row.label}
              onChange={() => { void handleToggleNotification(row.key) }}
            />
          }
        />
      ))}
    </SettingsCard>
    </>
  )

  const firmSection = (
    <>
      <SettingsCard icon="building" title="Firm" sub="The practice your account belongs to.">
        {firmName && <SettingsRow label="Firm">{firmName}</SettingsRow>}
        <SettingsRow
          label="Firm ID"
          action={
            <button type="button" onClick={() => { void handleCopyFirmId() }} className={S_BTN_GHOST_SM} aria-live="polite">
              <SIcon name={copied ? 'check' : 'copy'} size={14} />
              {copied ? 'Copied' : 'Copy'}
            </button>
          }
        >
          <span className="font-mono text-[13px] font-normal break-all">{authUser.firmId}</span>
        </SettingsRow>
        {memberSince && <SettingsRow label="Member since">{memberSince}</SettingsRow>}
      </SettingsCard>
      <SettingsCard
        icon="out"
        title="Session"
        sub="Sign out of Casevo on this device. You can sign back in at any time."
        right={
          <button type="button" onClick={signOut} className={S_BTN_DANGER_SM}>
            <SIcon name="out" size={14} />
            Sign out
          </button>
        }
      />
    </>
  )

  const sectionContent: Record<SettingsSection, React.ReactNode> = {
    account: accountSection,
    email: emailSection,
    appearance: appearanceSection,
    notifications: notificationsSection,
    firm: firmSection,
  }

  return (
    <div className="min-h-screen md:min-h-full bg-casevo-offwhite font-sans">
      <AppHeader
        onWordmarkClick={handleBack}
        leftHamburger={<SidebarHamburger />}
        leftSlot={<BackButton />}
      />

      <div className="max-w-[1180px] mx-auto px-4 sm:px-[36px] pt-6 sm:pt-[30px] pb-[60px]">
        {/* ── Page head (.phead) ── */}
        <div className="mb-[18px] lg:mb-[22px]">
          <p className="text-[12px] tracking-[0.18em] uppercase text-casevo-brand mb-[6px]">Settings</p>
          <h1 className="text-[22px] sm:text-[26px] font-medium tracking-[-0.01em] text-casevo-forest leading-[1.15]">{current.title}</h1>
          <p className="text-[13.5px] text-casevo-text-soft mt-[5px]">{current.sub}</p>
        </div>

        {/* ── Mobile / tablet: nav collapses to a scrollable tab bar ── */}
        <nav aria-label="Settings sections" className="lg:hidden -mx-4 px-4 mb-[16px] overflow-x-auto">
          <div className="flex gap-[6px] w-max pb-[2px]">
            {SECTIONS.map((s) => (
              <SectionNavPill key={s.key} icon={s.icon} label={s.label} active={activeSection === s.key} onClick={() => selectSection(s.key)} />
            ))}
          </div>
        </nav>

        {/* ── Split (.split): 236px nav | panel ── */}
        <div className="lg:grid lg:grid-cols-[236px_minmax(0,1fr)] lg:gap-[26px] lg:items-start">
          <nav aria-label="Settings sections" className="hidden lg:block sticky top-[20px]">
            <div className="flex gap-[10px] items-center px-[6px] pt-[4px] pb-[16px]">
              <UserAvatar userId={authUser.id} fallbackName={`${authUser.firstName} ${authUser.lastName}`.trim() || authUser.email} size="nav" title={false} />
              <div className="min-w-0">
                <b className="block text-[14px] font-semibold text-casevo-forest truncate">{fullName}</b>
                <small className="block text-[12px] text-casevo-text-soft truncate">
                  {[roleLabel, firmName].filter(Boolean).join(' · ')}
                </small>
              </div>
            </div>
            {SECTIONS.map((s) => (
              <SectionNavItem key={s.key} icon={s.icon} label={s.label} active={activeSection === s.key} sub={navSub(s.key)} onClick={() => selectSection(s.key)} />
            ))}
            <div className="h-px bg-casevo-border-soft mx-[6px] my-[10px]" />
            <button
              type="button"
              onClick={signOut}
              className="flex items-center gap-[11px] w-full text-left px-[12px] py-[10px] rounded-[10px] text-[14px] border border-transparent text-casevo-danger hover:bg-casevo-surface/60 transition-colors focus:outline-none casevo-focus"
            >
              <SIcon name="out" size={17} />
              Sign out
            </button>
          </nav>

          {/* ── Panel (.stack) ── */}
          <div className="flex flex-col gap-[18px] min-w-0">
            {sectionContent[activeSection]}
          </div>
        </div>
      </div>

      <AvatarChooserModal isOpen={avatarModalOpen} onClose={() => setAvatarModalOpen(false)} />
      {isAdmin ? (
        // ADMIN: direct self-change through the admin endpoint (own id).
        <EmailChangeDialog
          isOpen={emailRequestOpen}
          title="Change login email"
          intro="Your email is how you sign in. Changing it signs you out on every device — you'll sign straight back in with the new address. We'll email both addresses."
          currentEmail={authUser.email}
          submitLabel="Change email"
          busyLabel="Changing…"
          onSubmit={async (newEmail) => { await adminChangeUserEmailApi(authUser.id, newEmail) }}
          successContent={(newEmail) => (
            <>
              Your login email is now <strong className="font-semibold break-all">{newEmail}</strong>. Sign in again
              with it.
            </>
          )}
          successButtonLabel="Sign in again"
          onClose={() => setEmailRequestOpen(false)}
          // Our own sessions were revoked server-side → sign out cleanly.
          onDone={() => { setEmailRequestOpen(false); useCasevoStore.getState().clearAuth() }}
        />
      ) : (
        // MEMBER: request only → notifies the firm's admins (bell + email).
        <EmailChangeDialog
          isOpen={emailRequestOpen}
          title="Request email change"
          intro="Your email is how you sign in, so an admin makes the change. Enter the new address and we'll send the request to your firm's admins."
          currentEmail={authUser.email}
          submitLabel="Send request"
          busyLabel="Sending…"
          onSubmit={async (newEmail) => { await requestEmailChangeApi(newEmail) }}
          successContent={(newEmail) => (
            <>
              <strong className="font-semibold">Request sent to your admin.</strong> They&apos;ll change your login
              email to <strong className="font-semibold break-all">{newEmail}</strong>. You&apos;ll be signed out when
              they do — then sign in with the new address.
            </>
          )}
          onClose={() => setEmailRequestOpen(false)}
        />
      )}
      <ConfirmDialogue
        isOpen={signOutConfirmOpen}
        title="Sign out of Casevo?"
        message="You'll need to sign back in next time you visit."
        confirmLabel="Sign Out"
        onConfirm={() => useCasevoStore.getState().clearAuth()}
        onCancel={() => setSignOutConfirmOpen(false)}
      />
    </div>
  )
}

export default Settings
